<template>
  <div
    v-if="hasGoodConfiguration"
    class="gallery__child"
    :class="{
      'gallery__child--zoomable':
        !isVideo &&
        galleryState.desktop.itemsPositions[index].type !== 'one-per-line',
      'gallery__child--zoomed': zoomIndex === index,
    }"
    :style="style"
    @click="onClick"
  >
    <div v-if="isVideo" key="video" class="gallery__video">
      <client-only>
        <!-- <vimeo-player
          ref="player"
          :video-id="child.video_id"
          loop
          autoplay
          :options="{ muted: true, background: true }"
          :player-width="640"
          :player-height="800"
        /> -->

        <div style="position: relative; padding-top: 125%">
          <iframe
            :src="`https://iframe.mediadelivery.net/embed/${videoFolder}/${child.video.id}?autoplay=true&loop=true&muted=true&preload=true&responsive=true`"
            loading="lazy"
            style="
              border: 0;
              position: absolute;
              top: 0;
              height: 100%;
              width: 100%;
            "
            allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
            allowfullscreen="true"
          ></iframe>
        </div>
      </client-only>
    </div>
    <lazy-image
      v-else
      :images="
        getImages(
          child.image,
          galleryState.desktop.itemsPositions[index].type,
          galleryState.desktop.itemsPositions[index].format,
          child.image_cdn
        )
      "
      :force-show="galleryState.desktop.itemsPositions[index].lineNumber === 1"
      provider="sanity"
    />

    <lazy-image
      v-if="zoomIndex === index"
      :images="
        getImages(
          child.image,
          'one-per-line',
          galleryState.desktop.itemsPositions[index].format
        )
      "
      :force-show="true"
      class="gallery__child__image--zoomed"
      provider="sanity"
    />
  </div>
</template>

<script>
import galleryTools from '@/mixins/galleryTools'
import LazyImage from '@/components/Image/LazyImage'
import { BUNNY_STREAM_FOLDERS } from '@/const'

export default {
  name: 'GalleryChild',
  components: {
    LazyImage,
  },
  mixins: [galleryTools],
  props: {
    child: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    galleryState: {
      type: Object,
      required: true,
    },
    zoomIndex: {
      type: Number,
      default: -1,
    },
  },
  computed: {
    hasGoodConfiguration() {
      return (
        this.galleryState.desktop.itemsPositions[this.index] !== undefined &&
        this.galleryState.mobile.itemsPositions[this.index] !== undefined
      )
    },
    style() {
      return {
        '--grid-column':
          this.galleryState.mobile.itemsPositions[this.index].column,
        '--grid-row': this.galleryState.mobile.itemsPositions[this.index].row,
        '--grid-column-desktop':
          this.galleryState.desktop.itemsPositions[this.index].column,
        '--grid-row-desktop':
          this.galleryState.desktop.itemsPositions[this.index].row,
      }
    },
    isVideo() {
      return this.child._type === 'slider_video'
    },
    videoFolder() {
      return this.isVideo ? BUNNY_STREAM_FOLDERS[this.child.video.folder] : null
    },
  },
  methods: {
    onClick(e) {
      e.preventDefault()
      if (!this.child.is_video) {
        this.$emit(
          'zoom',
          this.galleryState.desktop.itemsPositions[this.index].lineNumber,
          this.index
        )
      }
    },
  },
}
</script>

<style lang="scss">
.gallery__child {
  position: relative;
  overflow: hidden;

  @include mq($until: tablet) {
    flex: 0 0 100vw;

    .gallery__inner--as-grid & {
      grid-column: var(--grid-column);
      grid-row: var(--grid-row);
      flex: none;
    }
  }

  @include mq($from: tablet) {
    grid-column: var(--grid-column-desktop);
    grid-row: var(--grid-row-desktop);

    > * {
      pointer-events: none;
    }
  }
}

.gallery__child--zoomable {
  cursor: url("data:image/svg+xml,%3Csvg width='25' height='25' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E %3Crect width='25' height='25' fill='white'/%3E %3Cpath d='M17.75 11.75H13.25V7.25C13.25 7.05109 13.171 6.86032 13.0303 6.71967C12.8897 6.57902 12.6989 6.5 12.5 6.5C12.3011 6.5 12.1103 6.57902 11.9697 6.71967C11.829 6.86032 11.75 7.05109 11.75 7.25V11.75H7.25C7.05109 11.75 6.86032 11.829 6.71967 11.9697C6.57902 12.1103 6.5 12.3011 6.5 12.5C6.5 12.6989 6.57902 12.8897 6.71967 13.0303C6.86032 13.171 7.05109 13.25 7.25 13.25H11.75V17.75C11.75 17.9489 11.829 18.1397 11.9697 18.2803C12.1103 18.421 12.3011 18.5 12.5 18.5C12.6989 18.5 12.8897 18.421 13.0303 18.2803C13.171 18.1397 13.25 17.9489 13.25 17.75V13.25H17.75C17.9489 13.25 18.1397 13.171 18.2803 13.0303C18.421 12.8897 18.5 12.6989 18.5 12.5C18.5 12.3011 18.421 12.1103 18.2803 11.9697C18.1397 11.829 17.9489 11.75 17.75 11.75Z' fill='%23002E5D'/%3E %3C/svg%3E")
      12 12,
    zoom-in;

  &.gallery__child--zoomed {
    cursor: url("data:image/svg+xml,%3Csvg width='25' height='25' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E %3Crect fill='%23FFFFFF' width='25' height='25'/%3E %3Cpath fill='%23002E5D' d='M7.2,11.8c-0.2,0-0.4,0.1-0.5,0.2c-0.1,0.1-0.2,0.3-0.2,0.5s0.1,0.4,0.2,0.5c0.1,0.1,0.3,0.2,0.5,0.2h10.5 c0.2,0,0.4-0.1,0.5-0.2c0.1-0.1,0.2-0.3,0.2-0.5s-0.1-0.4-0.2-0.5c-0.1-0.1-0.3-0.2-0.5-0.2H7.2z'/%3E %3C/svg%3E")
        12 12,
      zoom-out;
  }
}

.gallery__child__image--zoomed {
  position: absolute;
  inset: 0;
  z-index: 2;
  background-color: transparent !important;

  .lazy-image {
    background-color: transparent !important;

    &::after {
      content: none !important;
    }
  }

  picture {
    visibility: none;
    opacity: 0;
    transition: opacity 0.3s;

    &.ready {
      visibility: visible;
      opacity: 1;
    }
  }
}
</style>
